import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookmark,
  faBook,
  faClock,
  faRotate,
  faThumbsUp,
  faThumbsDown
} from '@fortawesome/free-solid-svg-icons';
import { faBookmark as faBookmarkRegular } from '@fortawesome/free-regular-svg-icons';

// Contexts
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { BundleContext } from "../../context/BundleProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";

// Components
import { GET_DOMAIN_NAME } from "../../api/content";

const ArticleContent = ({
  article,
  showHeader = true,
  showBookmark = true,
  showStats = true,
  showReaction = true,
  onClose = null
}) => {
  const { selectedCertificate } = useContext(BundleContext);
  const {
    addArticleToBookmark,
    removeArticleFromBookmark,
    getArticleBookmarks,
    markArticleAsRead,
    getArticleReadStatus,
    likeArticle,
    dislikeArticle,
    removeArticleReaction,
  } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const { trackArticleReaction } = useAnalytics();

  const [isLoading, setLoading] = useState(false);
  const [articleReadStatus, setArticleReadStatus] = useState(null);
  const [articleBookmarks, setArticleBookmarks] = useState([]);
  const [userReaction, setUserReaction] = useState(null); // 'like', 'dislike', or null
  const [isReactionLoading, setIsReactionLoading] = useState(false);
  const markedArticlesRef = useRef(new Set());

  // Memoize domain name lookup
  const article_topic_name = useMemo(() => {
    return GET_DOMAIN_NAME(selectedCertificate, article?.topic_id);
  }, [selectedCertificate, article?.topic_id]);

  // Memoize bookmark check
  const isBookmarked = useMemo(() => {
    return articleBookmarks.includes(article?.id);
  }, [articleBookmarks, article?.id]);

  // Memoize markdown styles - only recreate when theme changes
  const markdownStyles = useMemo(() => ({
    container: {
      padding: '1.5rem',
      background: theme?.background?.card,
      borderRadius: '1rem',
      margin: '1rem',
      border: `1px solid ${theme?.borders?.primary}`,
      boxShadow: theme?.shadows?.base,
    },
    content: {
      color: theme?.text?.primary,
      lineHeight: '1.7',
      fontSize: '1rem',
    },
    h1: {
      color: theme?.text?.primary,
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '1rem',
      marginTop: '0',
    },
    h2: {
      color: theme?.text?.primary,
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '0.75rem',
      marginTop: '1.5rem',
    },
    h3: {
      color: theme?.text?.primary,
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      marginTop: '1rem',
    },
    p: {
      marginBottom: '1rem',
      color: theme?.text?.primary,
    },
    ul: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem',
      listStyleType: 'disc',
    },
    li: {
      marginBottom: '0.5rem',
      color: theme?.text?.primary,
      display: 'list-item',
      listStylePosition: 'outside',
    },
    strong: {
      fontWeight: '600',
      color: theme?.text?.primary,
    },
    em: {
      fontStyle: 'italic',
      color: theme?.text?.primary,
    },
    code: {
      background: theme?.backgrounds?.tertiary,
      padding: '0.125rem 0.25rem',
      borderRadius: '0.25rem',
      fontSize: '0.875rem',
      color: theme?.text?.primary,
    },
    pre: {
      background: theme?.backgrounds?.tertiary,
      padding: '1rem',
      borderRadius: '0.5rem',
      overflow: 'auto',
      marginBottom: '1rem',
    },
    blockquote: {
      borderLeft: `4px solid ${theme?.primary?.blue?.[500]}`,
      paddingLeft: '1rem',
      marginLeft: '0',
      marginBottom: '1rem',
      fontStyle: 'italic',
      color: theme?.text?.secondary,
    },
    figcaption: {
      fontWeight: '600',
      fontSize: '0.75rem',
      fontStyle: 'italic',
      color: theme?.text?.tertiary,
      paddingBottom: '1rem',
      paddingLeft: '0.5rem',
    },
    actionButton: {
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '0.75rem',
      border: `1px solid ${theme?.borders?.primary}`,
      backgroundColor: theme?.backgrounds?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
    },
  }), [theme]);

  // Memoize ReactMarkdown components - only recreate when styles change
  const markdownComponents = useMemo(() => ({
    h1: ({children}) => <h1 style={markdownStyles.h1}>{children}</h1>,
    h2: ({children}) => <h2 style={markdownStyles.h2}>{children}</h2>,
    h3: ({children}) => <h3 style={markdownStyles.h3}>{children}</h3>,
    p: ({children}) => <p style={markdownStyles.p}>{children}</p>,
    ul: ({children}) => <ul style={markdownStyles.ul}>{children}</ul>,
    ol: ({children}) => <ol style={{...markdownStyles.ul, listStyleType: 'decimal'}}>{children}</ol>,
    li: ({children}) => <li style={markdownStyles.li}>{children}</li>,
    strong: ({children}) => <strong style={markdownStyles.strong}>{children}</strong>,
    em: ({children}) => <em style={markdownStyles.em}>{children}</em>,
    code: ({children}) => <code style={markdownStyles.code}>{children}</code>,
    pre: ({children}) => <pre style={markdownStyles.pre}>{children}</pre>,
    blockquote: ({children}) => <blockquote style={markdownStyles.blockquote}>{children}</blockquote>,
    figcaption: ({children}) => <figcaption style={markdownStyles.figcaption}>{children}</figcaption>,
    img: ({src, alt, ...props}) => (
      <img 
        src={src} 
        alt={alt} 
        style={{
          maxWidth: '500px',
          height: 'auto',
          margin: '1rem 0',
          borderRadius: '0.5rem',
          display: 'block'
        }}
        {...props}
      />
    ),
  }), [markdownStyles]);

  // Memoize formatted dates
  const formattedDates = useMemo(() => {
    if (!articleReadStatus) return null;
    return {
      firstRead: new Date(articleReadStatus.first_read_at).toLocaleDateString(),
      lastRead: articleReadStatus.read_count > 1 
        ? new Date(articleReadStatus.last_read_at).toLocaleDateString()
        : null
    };
  }, [articleReadStatus]);

  // Memoize bookmark handler
  const handleBookmark = useCallback(async () => {
    if (!article) return;

    try {
      setLoading(true);
      if (isBookmarked) {
        await removeArticleFromBookmark(article.id);
        setArticleBookmarks(prev => prev.filter(id => id !== article.id));
      } else {
        await addArticleToBookmark(article.id);
        setArticleBookmarks(prev => [...prev, article.id]);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      // Revert local state on error
      if (isBookmarked) {
        setArticleBookmarks(prev => [...prev, article.id]);
      } else {
        setArticleBookmarks(prev => prev.filter(id => id !== article.id));
      }
    } finally {
      setLoading(false);
    }
  }, [article, isBookmarked, addArticleToBookmark, removeArticleFromBookmark]);

  // Handle like button press
  const handleLikePress = useCallback(async () => {
    if (!article?.id || isReactionLoading) return;

    setIsReactionLoading(true);
    const previousReaction = userReaction;

    try {
      if (userReaction === 'like') {
        // User already liked, so remove the reaction
        setUserReaction(null);
        const result = await removeArticleReaction(article.id, article.topic_id);
        if (result.status) {
          // Track analytics
          trackArticleReaction(article.id, 'remove', {
            topic_id: article.topic_id,
            article_title: article.article_title,
            previous_reaction: 'like'
          });
        } else {
          // Revert on failure
          setUserReaction(previousReaction);
        }
      } else {
        // User is liking (either from dislike or no reaction)
        setUserReaction('like');
        const result = await likeArticle(article.id, article.topic_id);
        if (result.status) {
          // Track analytics
          trackArticleReaction(article.id, 'like', {
            topic_id: article.topic_id,
            article_title: article.article_title,
            previous_reaction: previousReaction
          });
        } else {
          // Revert on failure
          setUserReaction(previousReaction);
        }
      }
    } catch (error) {
      console.error('Error handling like:', error);
      setUserReaction(previousReaction);
    } finally {
      setIsReactionLoading(false);
    }
  }, [article, userReaction, isReactionLoading, likeArticle, removeArticleReaction, trackArticleReaction]);

  // Handle dislike button press
  const handleDislikePress = useCallback(async () => {
    if (!article?.id || isReactionLoading) return;

    setIsReactionLoading(true);
    const previousReaction = userReaction;

    try {
      if (userReaction === 'dislike') {
        // User already disliked, so remove the reaction
        setUserReaction(null);
        const result = await removeArticleReaction(article.id, article.topic_id);
        if (result.status) {
          // Track analytics
          trackArticleReaction(article.id, 'remove', {
            topic_id: article.topic_id,
            article_title: article.article_title,
            previous_reaction: 'dislike'
          });
        } else {
          // Revert on failure
          setUserReaction(previousReaction);
        }
      } else {
        // User is disliking (either from like or no reaction)
        setUserReaction('dislike');
        const result = await dislikeArticle(article.id, article.topic_id);
        if (result.status) {
          // Track analytics
          trackArticleReaction(article.id, 'dislike', {
            topic_id: article.topic_id,
            article_title: article.article_title,
            previous_reaction: previousReaction
          });
        } else {
          // Revert on failure
          setUserReaction(previousReaction);
        }
      }
    } catch (error) {
      console.error('Error handling dislike:', error);
      setUserReaction(previousReaction);
    } finally {
      setIsReactionLoading(false);
    }
  }, [article, userReaction, isReactionLoading, dislikeArticle, removeArticleReaction, trackArticleReaction]);

  // Load article read status and mark as read when article changes
  useEffect(() => {
    if (!article || !article.id) return;

    let isMounted = true;

    // Reset reaction state immediately when article changes to prevent showing previous article's reaction
    if (isMounted) {
      setUserReaction(null);
    }

    // Normalize articleId to string to ensure consistent type matching in markedArticlesRef
    const articleId = String(article.id);
    console.log("[ArticleContent] articleId", articleId, "type:", typeof article.id);

    // Check if we've already marked this article as read in this session
    if (markedArticlesRef.current.has(articleId)) {
      // Just load the read status without marking as read
      const loadReadStatus = async () => {
        try {
          console.log("[ArticleContent] Loading read status for previously viewed article:", articleId);
          const readStatusResult = await getArticleReadStatus(articleId);
          console.log("[ArticleContent] Read status result:", readStatusResult);
          
          if (isMounted && readStatusResult.status && readStatusResult.data) {
            setArticleReadStatus(readStatusResult.data);
            // Load reaction state from article data
            const reaction = readStatusResult.data.reaction || null;
            console.log("[ArticleContent] Setting reaction state:", reaction);
            setUserReaction(reaction);
          } else if (isMounted) {
            console.warn("[ArticleContent] No read status data found for article:", articleId);
            // If no data found, ensure reaction is null
            setUserReaction(null);
          }
        } catch (error) {
          if (isMounted) {
            console.error('Error loading article read status:', error);
            setUserReaction(null);
          }
        }
      };
      loadReadStatus();
      return () => {
        isMounted = false;
      };
    }

    // Mark article as read for the first time
    // Add to ref immediately to prevent duplicate calls if useEffect runs twice
    markedArticlesRef.current.add(articleId);
    
    const markAsRead = async () => {
      try {
        // Mark article as read - this already reads the article document internally
        const articleData = {
          topic_id: article.topic_id
        };
        console.log("[ArticleContent] Marking article as read - articleData", articleData);
        console.log("[ArticleContent] Marking article as read - articleId", articleId);

        const markReadResult = await markArticleAsRead(articleId, articleData);
        console.log("[ArticleContent] Mark read result:", markReadResult);
        
        if (isMounted && markReadResult.status && markReadResult.data?.article) {
          // Use the article data returned from markArticleAsRead (no need for separate read)
          setArticleReadStatus(markReadResult.data.article);
          // Load reaction state from article data
          const reaction = markReadResult.data.article.reaction || null;
          console.log("[ArticleContent] Setting reaction state from mark read:", reaction);
          setUserReaction(reaction);
        } else if (isMounted) {
          console.warn("[ArticleContent] Mark read result missing article data");
          // Remove from ref if the call failed so it can be retried
          markedArticlesRef.current.delete(articleId);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error handling article read status:', error);
          // Remove from ref if there was an error so it can be retried
          markedArticlesRef.current.delete(articleId);
        }
      }
    };

    markAsRead();
    
    return () => {
      isMounted = false;
    };
  }, [article?.id]); // Only depend on article ID

  // Fetch bookmarks from Firestore when component mounts
  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        const result = await getArticleBookmarks();
        if (result.status) {
          setArticleBookmarks(result.data);
        }
      } catch (error) {
        console.error('Error fetching article bookmarks:', error);
      }
    };
    
    fetchBookmarks();
  }, []); // Only run once when component mounts

  if (!article) {
    return null;
  }

  return (
    <div style={markdownStyles.container}>
      {/* Article Header */}
      {showHeader && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: `1px solid ${theme?.borders?.primary}`
        }}>
          <div style={{ flex: 1 }}>
            {/* Topic Badge */}
            <div style={{
              display: 'inline-block',
              padding: '0.5rem 1rem',
              background: theme?.primary?.blue?.[100] || '#dbeafe',
              color: theme?.primary?.blue?.[700] || '#1d4ed8',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              marginBottom: '1rem',
              border: `1px solid ${theme?.primary?.blue?.[200] || '#bfdbfe'}`
            }}>
              {article_topic_name}
            </div>
            <h1 style={markdownStyles.h1}>{article.article_title}</h1>
            {article.summary && (
              <p style={{ 
                ...markdownStyles.p, 
                color: theme?.text?.secondary,
                fontSize: '1.1rem',
                fontStyle: 'italic'
              }}>
                {article.summary}
              </p>
            )}
            {showStats && articleReadStatus && formattedDates && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.5rem 1rem',
                background: theme?.background?.tertiary,
                borderRadius: '0.5rem',
                border: `1px solid ${theme?.borders?.secondary}`,
                fontSize: '0.875rem',
                color: theme?.text?.secondary
              }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <span>
                    <FontAwesomeIcon icon={faBook} style={{ marginRight: '0.5rem' }} />
                    Read {articleReadStatus.read_count} time{articleReadStatus.read_count !== 1 ? 's' : ''}
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.5rem' }} />
                    First read: {formattedDates.firstRead}
                  </span>
                  {formattedDates.lastRead && (
                    <span>
                      <FontAwesomeIcon icon={faRotate} style={{ marginRight: '0.5rem' }} />
                      Last read: {formattedDates.lastRead}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Bookmark Button */}
          {showBookmark && (
            <motion.button
              style={markdownStyles.actionButton}
              whileHover={{
                scale: 1.05,
              }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBookmark}
              title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              <FontAwesomeIcon
                icon={isBookmarked ? faBookmark : faBookmarkRegular}
                style={{
                  color: isBookmarked
                    ? theme?.primary?.yellow?.[600]
                    : theme?.primary?.gray?.[500]
                }}
              />
            </motion.button>
          )}
        </div>
      )}

      {/* Article Body */}
      <div style={markdownStyles.content}>
        <ReactMarkdown
          rehypePlugins={[rehypeRaw]}
          components={markdownComponents}
        >
          {article.article_body}
        </ReactMarkdown>
      </div>

      {/* Key Takeaways Section */}
      {article.key_takeaways && article.key_takeaways.length > 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: theme?.primary?.blue?.[50] || '#f0f9ff',
          borderRadius: '1rem',
          border: `2px solid ${theme?.primary?.blue?.[200] || '#bfdbfe'}`,
          borderLeft: `4px solid ${theme?.primary?.blue?.[500] || '#3b82f6'}`
        }}>
          <h3 style={{
            color: theme?.primary?.blue?.[700] || '#1d4ed8',
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '1rem',
            marginTop: '0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <FontAwesomeIcon 
              icon={faBook} 
              style={{ color: theme?.primary?.blue?.[600] || '#2563eb' }}
            />
            Key Takeaways
          </h3>
          <ul style={{
            margin: '0',
            paddingLeft: '1.5rem',
            listStyleType: 'none'
          }}>
            {article.key_takeaways.map((takeaway, index) => (
              <li key={index} style={{
                marginBottom: '0.75rem',
                color: theme?.text?.primary,
                position: 'relative',
                paddingLeft: '1.5rem'
              }}>
                <span style={{
                  position: 'absolute',
                  left: '0',
                  top: '0.5rem',
                  width: '0.5rem',
                  height: '0.5rem',
                  background: theme?.primary?.blue?.[500] || '#3b82f6',
                  borderRadius: '50%',
                  transform: 'translateY(-50%)'
                }}></span>
                {takeaway}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Like/Dislike Reaction Buttons */}
      {showReaction && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          marginTop: '2rem',
          paddingTop: '1.5rem',
          borderTop: `1px solid ${theme?.borders?.primary}`
        }}>
          {/* Like Button */}
          <motion.button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '2rem',
              border: 'none',
              cursor: isReactionLoading ? 'not-allowed' : 'pointer',
              opacity: isReactionLoading ? 0.6 : 1,
              backgroundColor: userReaction === 'like'
                ? (theme?.primary?.blue?.[600] || '#2563eb')
                : (theme?.backgrounds?.tertiary || '#374151'),
              color: userReaction === 'like'
                ? '#ffffff'
                : (theme?.text?.primary || '#ffffff'),
              fontWeight: '600',
              fontSize: '1rem',
              minWidth: '120px',
            }}
            whileHover={!isReactionLoading ? { scale: 1.05 } : {}}
            whileTap={!isReactionLoading ? { scale: 0.95 } : {}}
            onClick={handleLikePress}
            disabled={isReactionLoading}
          >
            <FontAwesomeIcon icon={faThumbsUp} />
            {userReaction === 'like' ? 'Liked' : 'Like'}
          </motion.button>

          {/* Dislike Button */}
          <motion.button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '2rem',
              border: 'none',
              cursor: isReactionLoading ? 'not-allowed' : 'pointer',
              opacity: isReactionLoading ? 0.6 : 1,
              backgroundColor: userReaction === 'dislike'
                ? (theme?.primary?.blue?.[600] || '#2563eb')
                : (theme?.backgrounds?.tertiary || '#374151'),
              color: userReaction === 'dislike'
                ? '#ffffff'
                : (theme?.text?.primary || '#ffffff'),
              fontWeight: '600',
              fontSize: '1rem',
              minWidth: '120px',
            }}
            whileHover={!isReactionLoading ? { scale: 1.05 } : {}}
            whileTap={!isReactionLoading ? { scale: 0.95 } : {}}
            onClick={handleDislikePress}
            disabled={isReactionLoading}
          >
            <FontAwesomeIcon icon={faThumbsDown} />
            {userReaction === 'dislike' ? 'Disliked' : 'Dislike'}
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default ArticleContent; 